/**
 * `<SplitView/>` — the live two-way split (B2.4): the OUTLINE editor (left)
 * beside the TREE canvas (right), divided, both editing the *same* nodes.
 *
 * Issue #759 (Phase 2, PR-2 — the "two-way" hero completion). The promise
 * (§A7): one structure, two editors, kept in sync. This composes the SAME
 * `<OutlineEditor/>` the full-screen B2.3 uses with the SAME `<TaskCanvas/>`
 * the tree home (B2.1) uses — they read the same `useTaskNodes` data + the
 * same `treeStore` selection through props the parent passes identically to
 * both. So:
 *   - **Selection is synced** — selecting a node on either side highlights it
 *     on both (the shared `selectedId`).
 *   - **Editing on either side updates the other instantly** — both write
 *     through the same mutations; on success the query invalidates and both
 *     panes re-derive from one fresh source of truth.
 *
 * This component owns *only* the split chrome (panes, divider, section
 * headers, footers, the "same tree" pill — gallery copy verbatim). All edit
 * behaviour lives in the two children, so there's no forked logic to drift.
 *
 * Editorial revamp (#1043, Wave 1, B2.4): theme-aware chrome. EDITORIAL (flag
 * on) is the wireframe `V_B24` — a warm-paper OUTLINE pane (left, `c.rule`
 * right border) beside the already-editorial TREE canvas (right, from PR-A),
 * with the two "edit here, updates there" hints rendered as `.t-banner.info`
 * (a `--teal-soft` wash on a teal hairline) and mono pane labels. LEGACY (flag
 * off) is byte-identical to today. The canvas it composes is untouched
 * (already editorial from PR-A); only the split chrome forks on theme. Every
 * `data-testid` + copy is preserved.
 *
 * Resizable outline rail (#1043, founder prod feedback round 3, item 1): the
 * fixed `w-[42%]` outline pane is now USER-DRAGGABLE. A thin vertical gutter on
 * the outline⇄canvas boundary lets the lead drag the boundary left/right; the
 * canvas (`flex-1`) takes the rest. The chosen width is held in state, clamped
 * to a sensible min/max (so neither pane collapses), and PERSISTED to
 * localStorage (`tarii.map.outlineWidth`) so it sticks across reloads. The
 * absolutely-positioned "same tree" pill follows the live boundary (computed
 * `left`, no longer the hardcoded 42%). Drag is pointer-event-driven (mouse +
 * touch) with a `col-resize` cursor and a subtle editorial hover/active
 * emphasis. The PR-A collapse ("Hide outline" → canvas-only) is upstream in
 * `MapEditor` and is untouched — resize only applies while the outline shows.
 */

import { type ComponentProps, useCallback, useRef, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { PALETTE } from "../tree/palette";
import { OutlineEditor } from "./OutlineEditor";
import { TaskCanvas } from "./TaskCanvas";

export interface SplitViewProps {
  outline: ComponentProps<typeof OutlineEditor>;
  canvas: ComponentProps<typeof TaskCanvas>;
}

/** localStorage key for the persisted outline-rail width (in CSS pixels). */
export const OUTLINE_WIDTH_STORAGE_KEY = "tarii.map.outlineWidth";

/** Minimum outline-pane width — never let the outline collapse to unusable. */
export const OUTLINE_MIN_PX = 280;
/** Maximum outline-pane width, as a fraction of the container — keep the canvas usable. */
export const OUTLINE_MAX_FRACTION = 0.65;
/** Default outline width when nothing is persisted (the legacy 42% feel, in px on a ~960 split). */
export const OUTLINE_DEFAULT_PX = 400;
/** Keyboard arrow-key nudge step (px) for resizing the outline pane. */
export const OUTLINE_KEY_STEP_PX = 24;

/**
 * Clamp a desired outline width (px) into `[OUTLINE_MIN_PX, OUTLINE_MAX_FRACTION ×
 * container]`. When the container is unknown/zero (happy-dom rect, pre-layout)
 * only the floor applies — never collapse below the minimum.
 */
export function clampOutlineWidth(desiredPx: number, containerWidthPx: number): number {
  const max =
    containerWidthPx > 0
      ? Math.max(OUTLINE_MIN_PX, containerWidthPx * OUTLINE_MAX_FRACTION)
      : Number.POSITIVE_INFINITY;
  return Math.min(Math.max(desiredPx, OUTLINE_MIN_PX), max);
}

/** Read the persisted outline width (px), falling back to the default; tolerant of bad/absent values. */
export function readPersistedOutlineWidth(): number {
  try {
    const raw = window.localStorage.getItem(OUTLINE_WIDTH_STORAGE_KEY);
    if (raw == null) return OUTLINE_DEFAULT_PX;
    const px = Number.parseFloat(raw);
    return Number.isFinite(px) && px >= OUTLINE_MIN_PX ? px : OUTLINE_DEFAULT_PX;
  } catch {
    return OUTLINE_DEFAULT_PX;
  }
}

export function SplitView({ outline, canvas }: SplitViewProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // The chosen outline-pane width (CSS px), seeded from localStorage so it
  // sticks across reloads. Only the floor is enforced on read; the live
  // container-relative max is applied on every drag move (see `onPointerMove`).
  const [outlineWidth, setOutlineWidth] = useState<number>(() => readPersistedOutlineWidth());
  const [dragging, setDragging] = useState(false);
  const [gutterHover, setGutterHover] = useState(false);

  // The split container — its measured width is the basis for the clamp max.
  // happy-dom returns 0 here, so the clamp degrades to floor-only in tests.
  const containerRef = useRef<HTMLDivElement | null>(null);

  const persist = useCallback((px: number) => {
    try {
      window.localStorage.setItem(OUTLINE_WIDTH_STORAGE_KEY, String(Math.round(px)));
    } catch {
      // Private mode / quota — width still applies for the session.
    }
  }, []);

  const onPointerMove = useCallback((e: PointerEvent | React.PointerEvent) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    // Outline width = pointer X relative to the container's left edge.
    const desired = e.clientX - rect.left;
    setOutlineWidth(clampOutlineWidth(desired, rect.width));
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Left mouse button only (or touch/pen, which report button 0 too).
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setDragging(true);
      // Apply the press position immediately so a click without travel re-clamps.
      onPointerMove(e);
    },
    [onPointerMove],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      setDragging(false);
      // Persist the latest committed width.
      setOutlineWidth((w) => {
        persist(w);
        return w;
      });
    },
    [dragging, persist],
  );

  // Keyboard resize (WAI-ARIA window-splitter pattern): ←/→ nudge the boundary,
  // Home/End jump to the min / container-max. Keeps the handle usable without a
  // pointer + satisfies the focusable-interactive contract.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const delta =
        e.key === "ArrowLeft"
          ? -OUTLINE_KEY_STEP_PX
          : e.key === "ArrowRight"
            ? OUTLINE_KEY_STEP_PX
            : 0;
      const jumpToMin = e.key === "Home";
      const jumpToMax = e.key === "End";
      if (delta === 0 && !jumpToMin && !jumpToMax) return;
      e.preventDefault();
      const cw = containerRef.current?.getBoundingClientRect().width ?? 0;
      setOutlineWidth((w) => {
        const target = jumpToMin
          ? OUTLINE_MIN_PX
          : jumpToMax
            ? cw > 0
              ? cw * OUTLINE_MAX_FRACTION
              : w + OUTLINE_KEY_STEP_PX
            : w + delta;
        const next = clampOutlineWidth(target, cw);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  // The boundary tone: a calm editorial hairline, emphasised teal on hover/drag.
  const active = dragging || gutterHover;
  const gutterColor = editorial
    ? active
      ? c.teal
      : c.rule
    : active
      ? PALETTE.teal
      : PALETTE.softBorder;

  return (
    <div data-testid="split-view" ref={containerRef} className="relative flex min-h-0 flex-1">
      {/* ── OUTLINE pane (left) ───────────────────────────────────────── */}
      <div
        className="flex min-h-0 flex-col"
        style={{
          width: `${outlineWidth}px`,
          flex: "0 0 auto",
          backgroundColor: editorial ? c.canvas : PALETTE.cardBg,
        }}
        data-testid="split-outline-pane"
      >
        <div
          className="flex items-center justify-between px-5 py-2 text-[10px] font-bold uppercase tracking-wider"
          style={
            editorial
              ? {
                  backgroundColor: c.canvas,
                  color: c.muted,
                  fontFamily: theme.fonts.mono,
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  borderBottom: `1px solid ${c.rule}`,
                }
              : { backgroundColor: "#F7F8FA", color: "#6B7280" }
          }
        >
          Outline
          <span
            data-testid="split-outline-hint"
            className={editorial ? "normal-case" : "font-normal normal-case"}
            style={
              editorial
                ? {
                    backgroundColor: c.tealSoft,
                    border: "1px solid rgba(63,139,139,0.3)",
                    borderRadius: theme.radii.md,
                    color: c.tealInk,
                    padding: "4px 10px",
                    fontFamily: theme.fonts.sans,
                  }
                : { color: PALETTE.faint }
            }
          >
            ↕ edit here, tree updates instantly
          </span>
        </div>
        <div className="min-h-0 flex-1">
          <OutlineEditor {...outline} compact />
        </div>
      </div>

      {/* ── RESIZE gutter (draggable divider) ─────────────────────────── */}
      <div
        data-testid="split-resizer"
        // biome-ignore lint/a11y/useSemanticElements: there is no native splitter element; role="separator" + focus + arrow-key handling is the WAI-ARIA window-splitter pattern.
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label="Resize the outline pane"
        aria-valuemin={OUTLINE_MIN_PX}
        aria-valuenow={Math.round(outlineWidth)}
        className="relative z-10 flex-none cursor-col-resize touch-none select-none self-stretch"
        style={{ width: "7px", flex: "0 0 7px", backgroundColor: editorial ? c.canvas : "#FFFFFF" }}
        onPointerDown={onPointerDown}
        onPointerMove={(e) => {
          if (dragging) onPointerMove(e);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setGutterHover(true)}
        onMouseLeave={() => setGutterHover(false)}
      >
        {/* The visible hairline, centred in the wider hit area. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2"
          style={{
            width: active ? "2px" : "1px",
            backgroundColor: gutterColor,
            transition: "background-color 120ms ease, width 120ms ease",
          }}
        />
      </div>

      {/* ── TREE pane (right) ─────────────────────────────────────────── */}
      <div
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        style={{ backgroundColor: c.canvas }}
        data-testid="split-tree-pane"
      >
        <div
          className="flex items-center justify-between px-5 py-2 text-[10px] font-bold uppercase tracking-wider"
          style={
            editorial
              ? {
                  backgroundColor: c.canvas,
                  color: c.muted,
                  fontFamily: theme.fonts.mono,
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  borderBottom: `1px solid ${c.rule}`,
                }
              : { backgroundColor: "#F0F4F2", color: "#6B7280" }
          }
        >
          Tree
          <span
            data-testid="split-tree-hint"
            className={editorial ? "normal-case" : "font-normal normal-case"}
            style={
              editorial
                ? {
                    backgroundColor: c.tealSoft,
                    border: "1px solid rgba(63,139,139,0.3)",
                    borderRadius: theme.radii.md,
                    color: c.tealInk,
                    padding: "4px 10px",
                    fontFamily: theme.fonts.sans,
                  }
                : { color: PALETTE.faint }
            }
          >
            ← edit here, outline updates
          </span>
        </div>
        <div className="relative min-h-0 flex-1">
          <TaskCanvas {...canvas} />
        </div>
      </div>

      {/* "both panes — same tree" pill, floating near the divider — follows the
          live boundary (outline width + half the gutter), not the old 42%. */}
      <div
        data-testid="split-same-tree-pill"
        className="pointer-events-none absolute top-16 rounded-md border px-3 py-1 text-[10px] italic shadow-sm"
        style={{
          // Inside the tree pane, clear of the outline edge (#2705).
          left: `${outlineWidth + 7 + 12}px`,
          borderColor: editorial ? c.rule2 : PALETTE.softBorder,
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
          color: editorial ? c.muted : PALETTE.muted,
          fontFamily: editorial ? theme.fonts.sans : undefined,
        }}
      >
        both panes — same tree
      </div>
    </div>
  );
}
